/**
 * Aspect Ratio Calculator Tool
 *
 * Calculate aspect ratios or missing dimensions.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

function gcd(a: number, b: number): number {
  a = Math.abs(Math.round(a));
  b = Math.abs(Math.round(b));
  while (b) {
    [a, b] = [b, a % b];
  }
  return a;
}

function num(v: unknown, fallback: number): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export const AspectRatioCalculatorTool: ITransformerTool = {
  id: 'aspect-ratio-calculator',
  name: 'Aspect Ratio Calculator',
  description: 'Calculate aspect ratios or missing dimensions',
  version: '1.0.0',
  type: 'transformer',
  category: 'Utilities',
  keywords: ['aspect', 'ratio', 'width', 'height', 'resolution', 'calculator'],

  seo: {
    slug: 'aspect-ratio-calculator',
    pageTitle: 'Aspect Ratio Calculator - Free Online Tool',
    metaDescription: 'Calculate aspect ratios from width and height, or find missing dimensions from a ratio. Common presets included. Runs entirely in your browser.',
    keywords: ['aspect ratio calculator', 'screen ratio', 'video aspect ratio', 'image ratio calculator', 'resolution calculator'],
    relatedTools: ['dpi-calculator', 'percentage-calculator'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'mode',
      type: 'select',
      label: 'Mode',
      defaultValue: 'calculate-ratio',
      options: ['calculate-ratio', 'calculate-width', 'calculate-height'],
      description: 'Calculate ratio from dimensions, or find a missing dimension',
    },
    {
      name: 'width',
      type: 'number',
      label: 'Width',
      defaultValue: 1920,
      validation: { min: 1, max: 100000 },
      description: 'Width in pixels',
    },
    {
      name: 'height',
      type: 'number',
      label: 'Height',
      defaultValue: 1080,
      validation: { min: 1, max: 100000 },
      description: 'Height in pixels',
    },
    {
      name: 'ratioW',
      type: 'number',
      label: 'Ratio Width',
      defaultValue: 16,
      validation: { min: 1, max: 1000 },
      description: 'Aspect ratio width component (used for calculate-width/height)',
    },
    {
      name: 'ratioH',
      type: 'number',
      label: 'Ratio Height',
      defaultValue: 9,
      validation: { min: 1, max: 1000 },
      description: 'Aspect ratio height component (used for calculate-width/height)',
    },
  ],

  async transform(_input: string, config: Record<string, unknown>): Promise<string> {
    const mode = String(config.mode ?? 'calculate-ratio');
    const width = num(config.width, 0);
    const height = num(config.height, 0);
    const ratioW = num(config.ratioW, 16);
    const ratioH = num(config.ratioH, 9);

    switch (mode) {
      case 'calculate-ratio': {
        if (width <= 0 || height <= 0) throw ToolError.validation('Width and height must be positive');
        const divisor = gcd(width, height);
        const rW = width / divisor;
        const rH = height / divisor;
        const decimal = width / height;

        return [
          `Dimensions: ${width} × ${height}`,
          `Aspect Ratio: ${rW}:${rH}`,
          `Decimal: ${decimal.toFixed(4)}`,
          '',
          'Common ratios:',
          `  16:9 (${decimal >= 1.77 && decimal <= 1.78 ? '← match' : (16 / 9).toFixed(4)})`,
          `  4:3 (${decimal >= 1.33 && decimal <= 1.34 ? '← match' : (4 / 3).toFixed(4)})`,
          `  21:9 (${decimal >= 2.33 && decimal <= 2.34 ? '← match' : (21 / 9).toFixed(4)})`,
          `  1:1 (${decimal === 1 ? '← match' : '1.0000'})`,
        ].join('\n');
      }

      case 'calculate-width': {
        if (height <= 0 || ratioW <= 0 || ratioH <= 0) throw ToolError.validation('Height and ratio must be positive');
        const calculatedWidth = Math.round((height * ratioW) / ratioH);
        return [
          `Ratio: ${ratioW}:${ratioH}`,
          `Height: ${height}`,
          `Calculated Width: ${calculatedWidth}`,
          `Result: ${calculatedWidth} × ${height}`,
        ].join('\n');
      }

      case 'calculate-height': {
        if (width <= 0 || ratioW <= 0 || ratioH <= 0) throw ToolError.validation('Width and ratio must be positive');
        const calculatedHeight = Math.round((width * ratioH) / ratioW);
        return [
          `Ratio: ${ratioW}:${ratioH}`,
          `Width: ${width}`,
          `Calculated Height: ${calculatedHeight}`,
          `Result: ${width} × ${calculatedHeight}`,
        ].join('\n');
      }

      default:
        throw ToolError.validation(`Unknown mode: ${mode}`);
    }
  },
};
